<template>
    <div
        ref="root"
        class="product-gift-picker"
        :class="{ 'has-error': hasError, 'product-gift-picker--open': isOpen }"
    >
        <label v-if="label" class="control-label" :for="inputId">{{ label }}</label>

        <div v-if="disabled || readonly" class="form-control-static product-gift-picker__display">
            <template v-if="hasSelection">
                <strong>{{ modelValue }}</strong>
            </template>
            <span v-else class="text-muted">—</span>
        </div>

        <template v-else>
            <div ref="control" class="product-gift-picker__control">
                <button
                    v-if="!isOpen"
                    type="button"
                    class="form-control product-gift-picker__trigger text-left"
                    :class="{ 'product-gift-picker__trigger--placeholder': !hasSelection }"
                    :aria-expanded="isOpen"
                    :aria-controls="listboxId"
                    @click="open"
                >
                    <span v-if="hasSelection" class="product-gift-picker__trigger-text">{{ displayText }}</span>
                    <span v-else class="text-muted">{{ placeholder }}</span>
                </button>
                <input
                    v-else
                    :id="inputId"
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="form-control product-gift-picker__search"
                    role="combobox"
                    :aria-expanded="isOpen"
                    :aria-controls="listboxId"
                    aria-autocomplete="list"
                    :aria-activedescendant="activeDescendantId"
                    autocomplete="off"
                    :placeholder="placeholder"
                    @input="onSearchInput"
                    @keydown="onSearchKeydown"
                >
                <button
                    v-if="allowClear && hasSelection && !isOpen"
                    type="button"
                    class="product-gift-picker__clear"
                    :title="clearLabel"
                    @click.stop="clearSelection"
                >&times;</button>
            </div>

            <Teleport to="body">
                <div
                    v-if="isOpen"
                    ref="panel"
                    class="product-gift-picker__panel"
                >
                    <ul
                        :id="listboxId"
                        role="listbox"
                        class="product-gift-picker__list"
                    >
                        <li
                            v-if="searching"
                            class="product-gift-picker__message"
                            role="presentation"
                        >{{ searchingLabel }}</li>
                        <li
                            v-else-if="searchQuery.length < minLength"
                            class="product-gift-picker__message"
                            role="presentation"
                        >{{ typeToSearchLabel }}</li>
                        <li
                            v-else-if="candidateOptions.length === 0"
                            class="product-gift-picker__message"
                            role="presentation"
                        >{{ noResultsLabel }}</li>
                        <li
                            v-for="(item, index) in candidateOptions"
                            :id="optionId(item, index)"
                            :key="item.id"
                            role="option"
                            class="product-gift-picker__option"
                            :class="{
                                'is-active': activeIndex === index,
                                'is-selected': isSelectedOption(item),
                            }"
                            :aria-selected="isSelectedOption(item)"
                            @mousedown.prevent="selectItem(item)"
                            @mouseenter="activeIndex = index"
                        >
                            <strong>{{ item.ean }}</strong>
                            <span v-if="item.primaryArtist"> — {{ item.primaryArtist }}</span>
                            <span v-if="item.title"> — {{ item.title }}</span>
                        </li>
                    </ul>
                </div>
            </Teleport>
        </template>

        <input v-if="eanName" type="hidden" :name="eanName" :value="eanValue">
        <input v-if="productIdName" type="hidden" :name="productIdName" :value="productIdValue">
        <p v-if="errorMessage" class="help-block">{{ errorMessage }}</p>
    </div>
</template>

<script>
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';

export default {
    props: {
        modelValue: {
            type: String,
            default: '',
        },
        productId: {
            type: [Number, String, null],
            default: null,
        },
        primaryArtist: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        label: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: '',
        },
        searchUrl: {
            type: String,
            required: true,
        },
        inputId: {
            type: String,
            required: true,
        },
        eanName: {
            type: String,
            default: '',
        },
        productIdName: {
            type: String,
            default: '',
        },
        primaryArtistName: {
            type: String,
            default: '',
        },
        titleName: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        errorMessage: {
            type: String,
            default: '',
        },
        searchingLabel: {
            type: String,
            default: 'Searching…',
        },
        typeToSearchLabel: {
            type: String,
            default: 'Type at least 2 characters to search',
        },
        noResultsLabel: {
            type: String,
            default: 'No products found',
        },
        clearLabel: {
            type: String,
            default: 'Clear',
        },
        minLength: {
            type: Number,
            default: 2,
        },
        allowClear: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:modelValue', 'update:productId', 'update:primaryArtist', 'update:title', 'select'],
    data() {
        return {
            isOpen: false,
            searchQuery: '',
            results: [],
            searching: false,
            debounceTimer: null,
            activeIndex: -1,
            selectedItem: this.buildSelectedItemFromProps(),
            floatingCleanup: null,
        };
    },
    computed: {
        listboxId() {
            return `${this.inputId}-listbox`;
        },
        hasError() {
            return Boolean(this.errorMessage);
        },
        hasSelection() {
            return Boolean(String(this.modelValue || '').trim());
        },
        eanValue() {
            return this.modelValue || '';
        },
        productIdValue() {
            return this.productId ?? '';
        },
        displayText() {
            if (this.selectedItem?.text) {
                return this.selectedItem.text;
            }

            return this.formatDisplay(this.modelValue, this.primaryArtist, this.title);
        },
        candidateOptions() {
            const options = [...this.results];
            const selected = this.selectedItem;

            if (selected?.id && ! options.some((item) => item.id === selected.id)) {
                options.unshift(selected);
            }

            return options;
        },
        activeDescendantId() {
            if (this.activeIndex < 0 || ! this.candidateOptions[this.activeIndex]) {
                return '';
            }

            return this.optionId(this.candidateOptions[this.activeIndex], this.activeIndex);
        },
    },
    watch: {
        modelValue() {
            this.syncSelectedItemFromProps();
        },
        productId() {
            this.syncSelectedItemFromProps();
        },
        primaryArtist() {
            this.syncSelectedItemFromProps();
        },
        title() {
            this.syncSelectedItemFromProps();
        },
    },
    mounted() {
        document.addEventListener('mousedown', this.onDocumentMouseDown);
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        this.stopFloatingPosition();
        clearTimeout(this.debounceTimer);
    },
    methods: {
        optionId(item, index) {
            return `${this.inputId}-option-${item.id ?? index}`;
        },
        buildSelectedItemFromProps() {
            if (! this.modelValue && ! this.productId) {
                return null;
            }

            return {
                id: this.productId,
                ean: this.modelValue,
                primaryArtist: this.primaryArtist,
                title: this.title,
                text: this.formatDisplay(this.modelValue, this.primaryArtist, this.title),
            };
        },
        syncSelectedItemFromProps() {
            this.selectedItem = this.buildSelectedItemFromProps();
        },
        formatDisplay(ean, artist, title) {
            const parts = [ean, artist, title].filter((part) => String(part || '').trim() !== '');

            return parts.join(' — ');
        },
        isSelectedOption(item) {
            if (this.productId != null && item.id != null) {
                return String(item.id) === String(this.productId);
            }

            return String(item.ean || '') === String(this.modelValue || '');
        },
        open() {
            this.isOpen = true;
            this.searchQuery = this.modelValue || '';
            this.activeIndex = -1;

            if (this.searchQuery.length >= this.minLength) {
                this.fetchResults();
            } else {
                this.results = [];
            }

            this.$nextTick(() => {
                this.startFloatingPosition();
                this.$refs.searchInput?.focus();
            });
        },
        close() {
            this.isOpen = false;
            this.searching = false;
            this.activeIndex = -1;
            this.stopFloatingPosition();
            clearTimeout(this.debounceTimer);
        },
        startFloatingPosition() {
            this.stopFloatingPosition();

            const reference = this.$refs.control;
            const floating = this.$refs.panel;
            if (! reference || ! floating) {
                return;
            }

            floating.style.visibility = 'hidden';

            this.floatingCleanup = autoUpdate(reference, floating, () => {
                computePosition(reference, floating, {
                    placement: 'bottom-start',
                    strategy: 'fixed',
                    middleware: [
                        offset(2),
                        flip(),
                        shift({ padding: 8 }),
                        size({
                            apply({ rects, elements }) {
                                Object.assign(elements.floating.style, {
                                    width: `${rects.reference.width}px`,
                                });
                            },
                        }),
                    ],
                }).then(({ x, y }) => {
                    Object.assign(floating.style, {
                        position: 'fixed',
                        left: `${x}px`,
                        top: `${y}px`,
                        zIndex: '1050',
                        visibility: 'visible',
                    });
                });
            });
        },
        stopFloatingPosition() {
            if (this.floatingCleanup) {
                this.floatingCleanup();
                this.floatingCleanup = null;
            }
        },
        onDocumentMouseDown(event) {
            if (! this.isOpen) {
                return;
            }

            const inRoot = this.$refs.root?.contains(event.target);
            const inPanel = this.$refs.panel?.contains(event.target);

            if (! inRoot && ! inPanel) {
                this.close();
            }
        },
        onSearchInput() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.fetchResults(), 250);
        },
        onSearchKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.close();
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.moveActive(1);
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.moveActive(-1);
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                if (this.activeIndex >= 0 && this.candidateOptions[this.activeIndex]) {
                    this.selectItem(this.candidateOptions[this.activeIndex]);
                }
            }
        },
        moveActive(delta) {
            const count = this.candidateOptions.length;
            if (count === 0) {
                this.activeIndex = -1;
                return;
            }

            if (this.activeIndex < 0) {
                this.activeIndex = delta > 0 ? 0 : count - 1;
                return;
            }

            this.activeIndex = (this.activeIndex + delta + count) % count;
        },
        fetchResults() {
            if (this.searchQuery.length < this.minLength) {
                this.results = [];
                this.searching = false;
                return;
            }

            this.searching = true;
            axios.get(this.searchUrl, { params: { needle: this.searchQuery } })
                .then((response) => {
                    this.results = Array.isArray(response.data) ? response.data : [];
                    this.activeIndex = this.results.length > 0 ? 0 : -1;
                })
                .catch(() => {
                    this.results = [];
                    this.activeIndex = -1;
                })
                .finally(() => {
                    this.searching = false;
                });
        },
        selectItem(item) {
            this.applySelection(item);
            this.close();
        },
        applySelection(item) {
            const ean = String(item.ean || '');
            const artist = item.primaryArtist || '';
            const title = item.title || '';

            this.selectedItem = {
                ...item,
                text: item.text || this.formatDisplay(ean, artist, title),
            };
            this.$emit('update:modelValue', ean);
            this.$emit('update:productId', item.id ?? null);
            this.$emit('update:primaryArtist', artist);
            this.$emit('update:title', title);
            this.$emit('select', item);
        },
        clearSelection() {
            this.selectedItem = null;
            this.searchQuery = '';
            this.results = [];
            this.$emit('update:modelValue', '');
            this.$emit('update:productId', null);
            this.$emit('update:primaryArtist', '');
            this.$emit('update:title', '');
        },
    },
};
</script>

<style scoped>
.product-gift-picker__control {
    position: relative;
}

.product-gift-picker__trigger {
    padding-right: 30px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: #fff;
    cursor: pointer;
}

.product-gift-picker__trigger--placeholder {
    color: #999;
}

.product-gift-picker__clear {
    position: absolute;
    top: 0;
    right: 0;
    width: 30px;
    height: 34px;
    border: 0;
    background: transparent;
    color: #999;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
}

.product-gift-picker__clear:hover {
    color: #333;
}

.product-gift-picker__panel {
    background: #fff;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
}

.product-gift-picker__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 240px;
    overflow-y: auto;
}

.product-gift-picker__option,
.product-gift-picker__message {
    padding: 8px 12px;
    margin: 0;
}

.product-gift-picker__option {
    cursor: pointer;
}

.product-gift-picker__option.is-active,
.product-gift-picker__option:hover {
    background: #f5f5f5;
}

.product-gift-picker__option.is-selected {
    font-weight: 600;
}

.product-gift-picker__message {
    color: #777;
    font-style: italic;
}

.product-gift-picker__display {
    min-height: 34px;
    padding-top: 7px;
}
</style>
